import { useEffect } from 'react';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Anchor } from '@mantine/core';
import { clsx } from 'clsx';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

import { trackingHistorySectionMessages } from './tracking-history-section-messages';
import styles from './TrackingHistorySection.module.css';
import { FreightTrackingStatusId } from '../../freight-details-types';
import { activeTrackingStep, eventTimestampMillis, isShipmentException } from '../../freight-tracking-step';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlShipment } from '../../../../types/ltl';
import type { FreightTrackingEvent, FreightTrackingHistory } from '../../freight-details-types';
import type { MessageDescriptor } from 'react-intl';

type TrackingHistorySectionProps = {
  shipment: LtlShipment;
  trackingHistory?: FreightTrackingHistory | null;
  /** Fetches live tracking. Invoked automatically when cached events are stale relative to the entity's lastTrackingEventUtc. */
  onTrack?: () => void;
  /** Carrier name from the shipment entity, used as "Updated by" fallback when per-event carrierCode is null. */
  carrierName?: string | null;
};

const STEP_LABELS: MessageDescriptor[] = [
  trackingHistorySectionMessages.stepPendingPickup,
  trackingHistorySectionMessages.stepInTransit,
  trackingHistorySectionMessages.stepOutForDelivery,
  trackingHistorySectionMessages.stepDelivered,
];

/** Maps FreightTrackingStatusId to its display MessageDescriptor. */
const STATUS_MESSAGE_BY_ID: Partial<Record<number, MessageDescriptor>> = {
  [FreightTrackingStatusId.Unknown]: trackingHistorySectionMessages.statusUnknown,
  [FreightTrackingStatusId.Created]: trackingHistorySectionMessages.statusCreated,
  [FreightTrackingStatusId.PendingPickup]: trackingHistorySectionMessages.statusPendingPickup,
  [FreightTrackingStatusId.Dispatched]: trackingHistorySectionMessages.statusDispatched,
  [FreightTrackingStatusId.InRouteToPickup]: trackingHistorySectionMessages.statusInRouteToPickup,
  [FreightTrackingStatusId.AtPickup]: trackingHistorySectionMessages.statusAtPickup,
  [FreightTrackingStatusId.InTransit]: trackingHistorySectionMessages.statusInTransit,
  [FreightTrackingStatusId.OutForDelivery]: trackingHistorySectionMessages.statusOutForDelivery,
  [FreightTrackingStatusId.AtDelivery]: trackingHistorySectionMessages.statusAtDelivery,
  [FreightTrackingStatusId.Delivered]: trackingHistorySectionMessages.statusDelivered,
  [FreightTrackingStatusId.Exception]: trackingHistorySectionMessages.statusException,
  [FreightTrackingStatusId.Voided]: trackingHistorySectionMessages.statusVoided,
};

/**
 * Resolves the display label for a tracking event. Prefers the carrier-supplied description
 * when it meaningfully differs from the status label; falls back to the status label otherwise.
 */
const resolveEventLabel = (event: FreightTrackingEvent, statusLabel: string): string => {
  const description = (event.description ?? '').trim();
  if (description !== '' && description.toLowerCase() !== statusLabel.toLowerCase()) return description;
  return statusLabel !== '' ? statusLabel : description;
};

/**
 * Most recent event timestamp, shown as the header "Updated:" line and compared against the
 * shipment's `lastTrackingEventUtc` to detect a stale cache. Selects by instant but returns the
 * original string, so callers keep the raw value to format or re-parse. Unparseable timestamps
 * are dropped rather than echoed back.
 */
const latestEventTimestamp = (events: FreightTrackingEvent[]): string => {
  const timestamps = events.flatMap((event) => {
    const at = eventTimestampMillis(event.eventDateTimeUtc);
    return at === -Infinity ? [] : [{ raw: event.eventDateTimeUtc ?? '', at }];
  });

  if (timestamps.length === 0) return '';

  return timestamps.reduce((latest, current) => (current.at > latest.at ? current : latest)).raw;
};

const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('MM/dd/yyyy') : isoDate;
};

/** Header timestamp, e.g. `06/01/2026 5:23 pm`. Lowercasing the whole string only touches the meridiem. */
const formatDateTime = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('MM/dd/yyyy h:mm a').toLowerCase() : isoDate;
};

/** Time portion of an event timestamp, e.g. `7:30 am`. Empty when the value carries no time component. */
const formatTime = (isoDate: string): string => {
  if (!isoDate.includes('T')) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('h:mm a').toLowerCase() : '';
};

/**
 * Right-column Tracking card. Renders a four-step horizontal stepper
 * (Pending Pickup → In Transit → Out for Delivery → Delivered) driven by the
 * shipment's tracking status, plus the PRO Number external tracking link. The
 * header carries the newest carrier event time, and the Updates accordion
 * renders only when a `trackingHistory` prop with non-empty `events` is
 * supplied — a PRO number and updates are both absent early in the shipment
 * lifecycle. Store-agnostic.
 */
export const TrackingHistorySection = ({ shipment, trackingHistory, onTrack, carrierName }: TrackingHistorySectionProps) => {
  const intl = useIntl();

  const { lastTrackingEventUtc } = shipment;
  const events = trackingHistory?.events ?? [];
  const hasEvents = events.length > 0;
  const latestCachedTimestamp = latestEventTimestamp(events);
  const lastUpdated = formatDateTime(latestCachedTimestamp);

  const activeIndex = activeTrackingStep(shipment, events);
  useEffect(() => {
    if (!lastTrackingEventUtc || !onTrack) return;
    const entityTime = DateTime.fromISO(lastTrackingEventUtc, { zone: 'utc' });
    if (!entityTime.isValid) return;
    const isStale = latestCachedTimestamp === '' || entityTime > DateTime.fromISO(latestCachedTimestamp, { zone: 'utc' });
    if (isStale) onTrack();
  }, [lastTrackingEventUtc, latestCachedTimestamp, onTrack]);
  const isException = isShipmentException(shipment);
  const proNumber = shipment.proNumber ?? '';
  // Prefer the shipment entity's own trackingUrl (sourced with proNumber from the same freight-shipment
  // record) over trackingHistory's, which comes from a separate track-response payload and may be stale
  // or absent before the first track call resolves.
  const trackingUrl = shipment.trackingUrl ?? trackingHistory?.trackingUrl ?? undefined;

  return (
    <DetailSection
      headerRight={lastUpdated !== '' ? intl.formatMessage(trackingHistorySectionMessages.lastUpdated, { date: lastUpdated }) : undefined}
      testId='tracking-history-section'
      title={intl.formatMessage(trackingHistorySectionMessages.sectionTitle)}
    >
      <div className={styles.stepper}>
        {STEP_LABELS.map((label, index) => {
          const isCompleted = index < activeIndex;
          const isActive = index === activeIndex;

          const isExceptionStep = isActive && isException;

          return (
            <div className={clsx(styles.step, index > 0 && index <= activeIndex && styles.stepConnectorFilled)} key={label.id ?? index}>
              <div
                className={clsx(
                  styles.dot,
                  isCompleted && styles.dotCompleted,
                  isActive && !isExceptionStep && styles.dotActive,
                  isExceptionStep && styles.dotException,
                )}
                data-testid={`tracking-step-${index}`}
              >
                {!isCompleted && !isActive && (
                  <svg aria-hidden className={styles.dotDashedRing} viewBox='0 0 18 18'>
                    <circle cx='9' cy='9' fill='none' r='7' strokeDasharray='3 2.5' strokeLinecap='round' strokeWidth='2' />
                  </svg>
                )}
                {isCompleted && <FontAwesomeIcon className={styles.dotCheck} icon={faCheck} />}
                {isExceptionStep && <FontAwesomeIcon className={styles.dotIcon} icon={faTriangleExclamation} />}
              </div>
              <span
                className={clsx(
                  styles.stepLabel,
                  isCompleted && styles.stepLabelCompleted,
                  isActive && !isExceptionStep && styles.stepLabelActive,
                  isExceptionStep && styles.stepLabelException,
                )}
              >
                {intl.formatMessage(label)}
              </span>
            </div>
          );
        })}
      </div>

      {proNumber !== '' && (
        <div className={styles.proRow}>
          <span className={styles.proLabel}>{intl.formatMessage(trackingHistorySectionMessages.proNumberLabel)}</span>
          {trackingUrl ? (
            <Anchor className={styles.proLink} href={trackingUrl} rel='noopener noreferrer' target='_blank'>
              {proNumber}
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </Anchor>
          ) : (
            proNumber
          )}
        </div>
      )}

      {hasEvents && (
        <Accordion className={styles.updates} variant='contained'>
          <Accordion.Item value='updates'>
            <Accordion.Control>{intl.formatMessage(trackingHistorySectionMessages.updatesTitle)}</Accordion.Control>
            <Accordion.Panel>
              {events.map((event, index) => {
                const statusMsg =
                  event.freightTrackingStatusId != null
                    ? (STATUS_MESSAGE_BY_ID[event.freightTrackingStatusId] ?? trackingHistorySectionMessages.statusUnknown)
                    : null;
                const statusLabel = statusMsg ? intl.formatMessage(statusMsg) : '';
                const label = resolveEventLabel(event, statusLabel);
                const carrierCode = event.carrierCode ?? carrierName ?? '';
                const timestamp = event.eventDateTimeUtc ?? '';
                const date = formatDate(timestamp);
                const time = formatTime(timestamp);

                return (
                  <div className={styles.event} key={index}>
                    <div className={styles.eventText}>
                      {label !== '' && <span className={styles.eventStatus}>{label}</span>}
                      {carrierCode !== '' && (
                        <span className={styles.eventMeta}>
                          {intl.formatMessage(trackingHistorySectionMessages.updatedBy, { name: carrierCode })}
                        </span>
                      )}
                    </div>
                    {(date !== '' || time !== '') && (
                      <div className={styles.eventWhen}>
                        {date !== '' && <span className={styles.eventMeta}>{date}</span>}
                        {time !== '' && <span className={styles.eventMeta}>{time}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </DetailSection>
  );
};
